import React, { useState, ReactElement, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Row,
	PrimaryButton,
	ListItemCounter,
} from '@copyrightagent/basic-components'
import CreatedCaseList from 'components/CreatedCaseList'
import SubmittedCaseSearchAndFilter from 'components/SubmittedCaseSearchAndFilter'
import useSetPageTitle from 'hooks/useSetPageTitle'
import CreateCaseModal from 'components/CreateCaseModal/CreateCaseModal'
import { getCaseActions, useCase } from 'hooks/useCase'
import { RouteComponentProps, StaticContext, useParams } from 'react-router'
import { useLocation } from 'react-router-dom'
import {
	getSubmittedCaseStatusKey,
	parseSubmittedFilters,
} from 'lib/entities/submittedCaseStatus'
import { getSubmittedCasesCount } from './api'
import PageWithNavigation from '../HomeLayout/PageWithNavigation'

type Params = ReturnType<typeof useParams>

type CreateCaseProps = RouteComponentProps<
	{ [K in keyof Params]?: string },
	StaticContext,
	{ getStarted?: boolean }
>

const CreateCase = (props: CreateCaseProps): ReactElement => {
	const { t } = useTranslation()
	const location = useLocation()
	const [loaded, setLoaded] = useState<boolean>(false)
	const [showModal, setShowModal] = useState(false)
	const { submittedCases } = useCase()
	const { fetchSubmittedCases } = getCaseActions()

	const openModal = () => {
		setShowModal(true)
	}

	useEffect(() => {
		if (!props.location.state) return
		if (props.location.state.getStarted) setShowModal(true)
	}, [props.location])

	// Submitted cases are a partner's manual submissions (a small list), so we
	// load them all and search/filter client-side rather than paginating.
	useEffect(() => {
		const load = async () => {
			const result = await getSubmittedCasesCount()
			const count = Number(result.data.count) || 0
			if (count > 0) await fetchSubmittedCases(0, count)
			setLoaded(true)
		}
		load()
	}, [])

	// Filters are kept in the URL by the SearchBar.
	const filters = useMemo(
		() => parseSubmittedFilters(location.search),
		[location.search],
	)
	const isFiltering =
		filters.searchTerm.trim() !== '' || filters.status.length > 0

	// Newest submissions first, matching the date shown on each row.
	const filteredCases = useMemo(() => {
		const term = filters.searchTerm.trim().toLowerCase()
		return submittedCases
			.filter((theCase: any) => {
				const matchesUrl =
					!term || (theCase.url || '').toLowerCase().includes(term)
				const matchesStatus =
					filters.status.length === 0 ||
					filters.status.includes(getSubmittedCaseStatusKey(theCase))
				return matchesUrl && matchesStatus
			})
			.sort(
				(a: any, b: any) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
			)
	}, [submittedCases, filters])

	useSetPageTitle(t('createCase/pageTitle'))
	return (
		<PageWithNavigation title={t('createCase/pageTitle')}>
			{loaded && submittedCases.length > 0 ? (
				<>
					<SubmittedCaseSearchAndFilter />
					<Row
						justify="space-between"
						align="center"
						style={{ marginTop: 24, marginBottom: 8 }}
					>
						<ListItemCounter
							totalItemCount={filteredCases.length}
							title={t('createCase/title')}
							itemNameSingular={t('caseList/all/caseCount/caseName/singular')}
							itemNamePlural={t('caseList/all/caseCount/caseName/plural')}
						/>
						<PrimaryButton
							type="button"
							text={t('createCase/submit/title')}
							onClick={openModal}
						/>
					</Row>
				</>
			) : (
				<></>
			)}
			<CreatedCaseList
				cases={filteredCases}
				loadNextCases={() => undefined}
				hasMore={false}
				createCase={openModal}
				loaded={loaded}
				isFiltered={isFiltering}
			/>
			<CreateCaseModal opened={showModal} setDialogOpened={setShowModal} />
		</PageWithNavigation>
	)
}
export default CreateCase
